import { Gauge, Tag, Users } from 'lucide-react'

import type { FilterDefinition } from '@/components/filters/list-filter-types'
import { FilterOptionList, type FilterOption } from '@/components/filters/filter-menu'
import { VisibleEnumSelect } from '@/components/filters/visible-filter-field-controls'
import { formatMultiValueChipDisplayFromLabels } from '@/lib/list-filters/list-filter-params'

export const MEMORIES_DEFAULT_SORT = 'created_at_desc' as const

export type MemoriesSort =
  | 'created_at_desc'
  | 'created_at_asc'
  | 'importance_desc'
  | 'importance_asc'

export const MEMORIES_SORT_OPTIONS: FilterOption[] = [
  { value: 'created_at_desc', label: 'Newest first' },
  { value: 'created_at_asc', label: 'Oldest first' },
  { value: 'importance_desc', label: 'Highest importance' },
  { value: 'importance_asc', label: 'Lowest importance' },
]

export type MemoriesFilterState = {
  searchTerm: string
  categoryFilter: string
  importanceFilter: string
  sortBy: string
  selectedEntity: { id: string; name: string }[]
}

export type MemoriesFilterCtx = {
  categoryOptions: FilterOption[]
  importanceOptions: FilterOption[]
}

export function getMemoriesFilterDefinitions(): FilterDefinition<
  MemoriesFilterState,
  MemoriesFilterCtx
>[] {
  return [
    {
      key: 'category',
      label: 'Category',
      icon: Tag,
      displayValue: (filters, ctx) => {
        if (filters.categoryFilter === 'all') return 'All'
        return (
          ctx.categoryOptions.find((o) => o.value === filters.categoryFilter)?.label ??
          filters.categoryFilter
        )
      },
      isNonDefault: (filters) => filters.categoryFilter !== 'all',
      reset: (filters) => ({ ...filters, categoryFilter: 'all' }),
      renderField: ({ filters, onChange, ctx }) => (
        <VisibleEnumSelect
          value={filters.categoryFilter}
          options={ctx.categoryOptions}
          onValueChange={(v) => onChange({ ...filters, categoryFilter: v })}
        />
      ),
      renderPopover: ({ filters, onChange, ctx }) => (
        <FilterOptionList
          type="single"
          options={ctx.categoryOptions}
          value={filters.categoryFilter}
          onValueChange={(v) => onChange({ ...filters, categoryFilter: v })}
          searchPlaceholder="Search categories…"
        />
      ),
    },
    {
      key: 'importance',
      label: 'Importance',
      icon: Gauge,
      displayValue: (filters, ctx) => {
        if (filters.importanceFilter === 'all') return 'All'
        return (
          ctx.importanceOptions.find((o) => o.value === filters.importanceFilter)?.label ??
          filters.importanceFilter
        )
      },
      isNonDefault: (filters) => filters.importanceFilter !== 'all',
      reset: (filters) => ({ ...filters, importanceFilter: 'all' }),
      renderField: ({ filters, onChange, ctx }) => (
        <VisibleEnumSelect
          value={filters.importanceFilter}
          options={ctx.importanceOptions}
          onValueChange={(v) => onChange({ ...filters, importanceFilter: v })}
        />
      ),
      renderPopover: ({ filters, onChange, ctx }) => (
        <FilterOptionList
          type="single"
          options={ctx.importanceOptions}
          value={filters.importanceFilter}
          onValueChange={(v) => onChange({ ...filters, importanceFilter: v })}
        />
      ),
    },
    {
      key: 'entity',
      label: 'Entity',
      icon: Users,
      displayValue: (filters) =>
        formatMultiValueChipDisplayFromLabels(filters.selectedEntity.map((entity) => entity.name)),
      isNonDefault: (filters) => filters.selectedEntity.length > 0,
      reset: (filters) => ({ ...filters, selectedEntity: [] }),
      // Entity stays card-click only — not on Visible Fields layout (≤2 fields).
      renderPopover: ({ filters, onChange }) => (
        <div className="space-y-2 text-sm">
          {filters.selectedEntity.length === 0 ? (
            <p className="text-muted-foreground">
              Click an entity on a memory card to add this filter.
            </p>
          ) : (
            <ul className="space-y-1">
              {filters.selectedEntity.map((entity) => (
                <li key={entity.id} className="flex items-center justify-between gap-2">
                  <span className="truncate">{entity.name}</span>
                  <button
                    type="button"
                    className="shrink-0 text-xs text-muted-foreground hover:text-foreground"
                    onClick={() =>
                      onChange({
                        ...filters,
                        selectedEntity: filters.selectedEntity.filter((e) => e.id !== entity.id),
                      })
                    }
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ),
    },
  ]
}
